import {
  WorkspaceLayout,
  WorkspaceContent,
  WorkspaceSidebar,
  WorkspaceSidebarHeader,
  WorkspaceSidebarNav,
  WorkspaceSidebarGroup,
  WorkspaceSidebarLink,
  WorkspaceSidebarPageMenu,
} from "@papier/ui/components/ui/workspace-sidebar";
import { use, type ReactNode } from "react";
import { Link, linkOptions } from "@tanstack/react-router";
import { AuthUIContext, UserAvatar } from "@papier/auth-ui";
import {
  ArrowLeft,
  Buildings2 as Building2,
  ShieldCheck,
  UserRounded as UserRound,
} from "@papier/ui/components/icons";
import { DropdownMenuItem, DropdownMenuLabel } from "@papier/ui/components/ui/dropdown-menu";

export const accountPages = linkOptions([
  {
    to: "/account/$accountView",
    params: { accountView: "settings" },
    label: "Profile",
    icon: UserRound,
    description: "Manage the personal details and email address associated with your account.",
  },
  {
    to: "/account/$accountView",
    params: { accountView: "security" },
    label: "Security",
    icon: ShieldCheck,
    description: "Protect your account and manage how you sign in to Papier.",
  },
  {
    to: "/account/$accountView",
    params: { accountView: "organizations" },
    label: "Organizations",
    icon: Building2,
    description: "Manage your workspaces and respond to invitations from other teams.",
  },
]);

export function AccountWorkspace({ view, children }: { view: string; children: ReactNode }) {
  const {
    hooks: { useSession },
  } = use(AuthUIContext);
  const { data: session, isPending } = useSession();
  const current = accountPages.find((page) => page.params.accountView === view) ?? accountPages[0];
  return (
    <WorkspaceLayout>
      <a
        href="#account-content"
        className="sr-only z-50 rounded-lg bg-background p-3 focus:not-sr-only focus:absolute"
      >
        Skip to account settings
      </a>
      <WorkspaceSidebar
        aria-label="Account workspace"
        header={
          <WorkspaceSidebarHeader
            backLink={
              <Link to="/projects">
                <ArrowLeft aria-hidden />
                Back to projects
              </Link>
            }
            avatar={<UserAvatar user={session?.user} isPending={isPending} />}
            label="Personal account"
            name={session?.user.name ?? "Your account"}
          />
        }
        mobileNavigation={
          <WorkspaceSidebarPageMenu
            label={current.label}
            icon={<current.icon aria-hidden />}
            ariaLabel="Account pages"
          >
            <DropdownMenuLabel>Account settings</DropdownMenuLabel>
            {accountPages.map((page) => (
              <DropdownMenuItem asChild key={page.params.accountView}>
                <Link
                  to={page.to}
                  params={page.params}
                  activeOptions={{ exact: true, includeSearch: false }}
                  activeProps={{ "aria-current": "page" }}
                >
                  <page.icon className="size-4" aria-hidden />
                  {page.label}
                </Link>
              </DropdownMenuItem>
            ))}
          </WorkspaceSidebarPageMenu>
        }
        footer={
          <p className="rounded-xl border bg-muted/20 p-4 text-xs leading-relaxed text-muted-foreground">
            Your profile and security settings apply to every organization you belong to.
          </p>
        }
      >
        <WorkspaceSidebarNav aria-label="Account navigation">
          <WorkspaceSidebarGroup label="Account">
            {accountPages.map((page) => (
              <WorkspaceSidebarLink asChild key={page.params.accountView}>
                <Link
                  to={page.to}
                  params={page.params}
                  activeOptions={{ exact: true, includeSearch: false }}
                  activeProps={{ "aria-current": "page" }}
                >
                  <page.icon aria-hidden />
                  {page.label}
                </Link>
              </WorkspaceSidebarLink>
            ))}
          </WorkspaceSidebarGroup>
        </WorkspaceSidebarNav>
      </WorkspaceSidebar>
      <WorkspaceContent id="account-content">
        <main className="w-full min-w-0 space-y-8 sm:space-y-10">
          <header>
            <p className="mb-2 text-[11px] font-medium text-muted-foreground">Account settings</p>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{current.label}</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              {current.description}
            </p>
          </header>
          {children}
        </main>
      </WorkspaceContent>
    </WorkspaceLayout>
  );
}
